/**
 * RAM — PoaSuspendRevoke action.
 *
 * Records a POA, suspend, or revoke action via CustMod (CIF-level flag).
 * This is a simplified UI stub — the authoritative field list is still being
 * refined (procedures digest §RAM "POA/Suspend/Revoke").
 */
import { useState } from 'react';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { asIso8601 } from '@retail-ops/shared-types';
import type { CustModRequest, SessionEventPayload } from '@retail-ops/shared-types';
import type { RamTarget } from '../types';

export interface PoaSuspendRevokeProps {
  target: RamTarget;
  onDone: () => void;
  onCancel: () => void;
}

type PoaMode = 'poa-grant' | 'suspend' | 'revoke';

export default function PoaSuspendRevoke({
  target,
  onDone,
  onCancel,
}: PoaSuspendRevokeProps): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();

  const [mode, setMode] = useState<PoaMode>('poa-grant');
  const [agentName, setAgentName] = useState<string>('');
  const [effectiveDate, setEffectiveDate] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<boolean>(false);

  const canSave =
    !saving &&
    target.cifId.length > 0 &&
    (mode !== 'poa-grant' || agentName.trim().length > 0);

  const handleSave = async (): Promise<void> => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const req: CustModRequest = {
        CifId: target.cifId,
        AuthMethod: 'InPerson',
        OffCode: '000',
        AuditNotesEnabled: true,
      };
      const res = await client.accounts.custMod(req);

      const ev: SessionEventPayload = {
        eventType: 'cif-modified',
        eventCategory: 'cif-account',
        severity: 'info',
        actor: user?.upn ?? 'fsr',
        timestamp: asIso8601(new Date().toISOString()),
        payloadJson: JSON.stringify({
          action: mode,
          cifId: target.cifId,
          agentName: mode === 'poa-grant' ? agentName : undefined,
          effectiveDate: effectiveDate || undefined,
          notes: notes || undefined,
        }),
      };
      client.retail.events.write(ev).catch(() => undefined);

      if (!res.success) {
        setError(res.error ?? 'CustMod returned a non-success result.');
        setSaving(false);
        return;
      }
      setDone(true);
      setSaving(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unable to record action.');
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="ram-wizard">
        <Banner tone="ok">
          Recorded {mode} action against CIF {target.cifId}.
        </Banner>
        <div className="ram-wizard__actions">
          <Button variant="primary" onClick={onDone}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ram-wizard">
      <div className="ram-wizard__header">
        <h2 className="ram-wizard__title">POA / Suspend / Revoke</h2>
        <Pill variant="info">CIF {target.cifId}</Pill>
      </div>

      <Banner tone="warn">
        Capture the executed document (POA instrument, revocation letter, etc.)
        in RDL before submitting — it is required by audit.
      </Banner>

      <div className="ram-wizard__fields">
        <label className="ram-search__label">
          Action
          <select
            className="ram-input"
            value={mode}
            onChange={(e) => setMode(e.target.value as PoaMode)}
          >
            <option value="poa-grant">Grant power-of-attorney</option>
            <option value="suspend">Suspend</option>
            <option value="revoke">Revoke</option>
          </select>
        </label>
        {mode === 'poa-grant' && (
          <label className="ram-search__label">
            Agent (attorney-in-fact) name
            <input
              className="ram-input"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              maxLength={80}
            />
          </label>
        )}
        <label className="ram-search__label">
          Effective date
          <input
            className="ram-input"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
          />
        </label>
        <label className="ram-search__label" style={{ gridColumn: '1 / -1' }}>
          Notes
          <textarea
            className="ram-input"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
          />
        </label>
      </div>

      {error && <Banner tone="err">{error}</Banner>}

      <div className="ram-wizard__actions">
        <Button variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {saving ? 'Recording…' : 'Record action'}
        </Button>
      </div>
    </div>
  );
}
